import { Checkbox, CheckboxField } from '../../catalyst/checkbox'
import { Description, Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { Textarea } from '../../catalyst/textarea'
import { dropNulls } from '../../lib/api'
import { BUILDING_CHECKLIST, BUILDING_SECTIONS, HYGIENE_FACILITY_CHECKLIST, HYGIENE_SECTIONS } from '../../lib/canadagap-checklists'
import { ChecklistFieldset, type ChecklistValue } from '../../lib/checklist-fieldset'
import { useLogNames } from '../../lib/farm-log-names'
import { AssetNameInput } from '../../lib/asset-picker'
import { assetIdFor, useFarmAssets } from '../../lib/inventory'
import { NameInput } from '../../lib/name-input'
import type { AssetCategory, LogNameKind } from '../../lib/types'
import { ChoiceButtons, YesNo } from '../chips'
import { optNum, optText, str, type FlowDef, type StepCtx } from '../flow'
import { rememberWho, WhoField } from '../who'
import { cap, farmPath, line, TextField } from './shared'

// The farm-level logs: not per field, just a date. Each is one short flow.

/** A name the farm reuses (building, facility, equipment, trap), suggesting what it used before. */
function NameField({ ctx, label, kind, valueKey, building }: { ctx: StepCtx; label: string; kind: LogNameKind; valueKey: string; building?: string }) {
  const { names } = useLogNames(ctx.orgId, ctx.farmId, kind, building)
  return (
    <FormField>
      <Label>{label}</Label>
      <NameInput suggestions={names} value={str(ctx.values[valueKey])} onChange={(e) => ctx.patch({ [valueKey]: e.target.value })} />
    </FormField>
  )
}

/**
 * An equipment or building name that can be picked from the inventory: the draft keeps the name and,
 * when it is an inventory asset's, that asset's id as `assetId` (cleared when the name no longer is).
 */
function AssetField({ ctx, label, kind, category, valueKey }: { ctx: StepCtx; label: string; kind: LogNameKind; category: AssetCategory; valueKey: string }) {
  const { names } = useLogNames(ctx.orgId, ctx.farmId, kind)
  const { assets } = useFarmAssets(ctx.orgId, ctx.farmId, category)
  const name = str(ctx.values[valueKey])
  const linked = optText(ctx.values.assetId) ?? null
  return (
    <FormField>
      <Label>{label}</Label>
      <AssetNameInput
        assets={assets}
        pastNames={names}
        linkedAssetId={linked}
        inventoryHref={`/farms/${ctx.farmId}/inventory`}
        value={name}
        onChange={(text) => ctx.patch({ [valueKey]: text, assetId: assetIdFor(assets, text) })}
      />
    </FormField>
  )
}

/** The linked asset, if any, for an entry body (left out when there is none). */
const assetOf = (v: StepCtx['values']) => optText(v.assetId) ?? null
/** " · from inventory" on a review line when the entry is linked. */
const linkedNote = (v: StepCtx['values']) => (assetOf(v) ? ' · from inventory' : '')

const Yes = (b: unknown) => (b === true ? 'Yes' : 'No')
const checklistOf = (v: StepCtx['values']): ChecklistValue => (v.checklist && typeof v.checklist === 'object' ? (v.checklist as ChecklistValue) : {})
const answered = (c: ChecklistValue) => Object.values(c).filter(Boolean).length
const issues = (c: ChecklistValue) => Object.values(c).filter((a) => a === 'not_ok').length

// --------------------------------------------------------------------------- Form I

const ACTIVITY_TYPES = ['cleaning', 'maintenance', 'calibration', 'repair', 'inspection', 'other'] as const

export const equipmentFlow: FlowDef = {
  kind: 'equipment',
  checkForm: 'i',
  label: 'Equipment work',
  needsField: false,
  initial: ({ today }) => ({ date: today }),
  steps: [
    {
      id: 'what',
      title: 'What did you do to the equipment?',
      valid: (v) => str(v.equipmentName).trim() !== '' && str(v.activityType) !== '',
      render: (ctx) => (
        <FieldGroup>
          <AssetField ctx={ctx} label="Which equipment?" kind="equipment" category="EQUIPMENT" valueKey="equipmentName" />
          <ChoiceButtons label="What was done?" value={str(ctx.values.activityType) as (typeof ACTIVITY_TYPES)[number] | ''} options={ACTIVITY_TYPES.map((t) => ({ value: t, label: cap(t) }))} onChange={(t) => ctx.patch({ activityType: t })} />
          <TextField ctx={ctx} label="What happened?" valueKey="description" multiline />
          <WhoField ctx={ctx} label="Who did it?" valueKey="performedBy" />
        </FieldGroup>
      ),
    },
  ],
  review: ({ values }) => ({
    title: 'Equipment work',
    lines: [{ label: 'Date', value: str(values.date) }, { label: 'Equipment', value: str(values.equipmentName) + linkedNote(values) }, { label: 'Work', value: cap(str(values.activityType)) }, ...line('Details', values.description), ...line('Done by', values.performedBy)],
  }),
  entry: (ctx) => {
    const v = ctx.values
    return dropNulls({ performedOn: str(v.date), equipmentName: str(v.equipmentName).trim(), assetId: assetOf(v), activityType: str(v.activityType), description: optText(v.description) ?? null, performedBy: optText(v.performedBy) ?? null })
  },
  badge: { framework: 'CanadaGAP', formCode: 'I', formName: 'Equipment Cleaning, Maintenance and Calibration' },
  historyPath: farmPath('equipment-activities'),
  onRecorded: rememberWho('performedBy'),
}

// --------------------------------------------------------------------------- Form G

export const buildingInspectionFlow: FlowDef = {
  kind: 'building_inspection',
  checkForm: 'g',
  label: 'Building inspection',
  needsField: false,
  initial: ({ today }) => ({ date: today, checklist: {} }),
  steps: [
    {
      id: 'building',
      title: 'Which building?',
      valid: (v) => str(v.buildingName).trim() !== '',
      render: (ctx) => (
        <FieldGroup>
          <AssetField ctx={ctx} label="Building" kind="building" category="BUILDING" valueKey="buildingName" />
          <WhoField ctx={ctx} label="Inspected by" valueKey="completedBy" />
        </FieldGroup>
      ),
    },
    {
      id: 'checklist',
      title: 'What did you find?',
      valid: () => true,
      render: ({ values, patch }) => (
        <ChecklistFieldset sections={BUILDING_SECTIONS} items={BUILDING_CHECKLIST} value={checklistOf(values)} onChange={(c) => patch({ checklist: c })} />
      ),
    },
    {
      id: 'work',
      title: 'Anything that needs fixing?',
      valid: () => true,
      render: (ctx) => (
        <FieldGroup>
          <TextField ctx={ctx} label="Inside work needed" valueKey="interiorWorkNeeded" multiline />
          <TextField ctx={ctx} label="Outside work needed" valueKey="exteriorWorkNeeded" multiline />
        </FieldGroup>
      ),
    },
  ],
  review: ({ values }) => {
    const c = checklistOf(values)
    return {
      title: `Building inspection · ${str(values.buildingName)}`,
      lines: [
        { label: 'Date', value: str(values.date) },
        { label: 'Building', value: str(values.buildingName) + linkedNote(values) },
        { label: 'Checked', value: `${answered(c)} of ${BUILDING_CHECKLIST.length} items, ${issues(c)} with an issue` },
        ...line('Inspected by', values.completedBy),
        ...line('Inside work', values.interiorWorkNeeded),
        ...line('Outside work', values.exteriorWorkNeeded),
      ],
    }
  },
  entry: (ctx) => {
    const v = ctx.values
    return dropNulls({ buildingName: str(v.buildingName).trim(), assetId: assetOf(v), inspectedOn: str(v.date), checklist: checklistOf(v), completedBy: optText(v.completedBy) ?? null, interiorWorkNeeded: optText(v.interiorWorkNeeded) ?? null, exteriorWorkNeeded: optText(v.exteriorWorkNeeded) ?? null })
  },
  badge: { framework: 'CanadaGAP', formCode: 'G', formName: 'Cleaning, Maintenance and Repair of Buildings' },
  historyPath: farmPath('building-inspections'),
  onRecorded: rememberWho('completedBy'),
}

// --------------------------------------------------------------------------- Form J

export const hygieneFlow: FlowDef = {
  kind: 'hygiene',
  checkForm: 'j',
  label: 'Hygiene facility check',
  needsField: false,
  initial: ({ today }) => ({ date: today, checklist: {} }),
  steps: [
    {
      id: 'facility',
      title: 'Which facility?',
      valid: (v) => str(v.facilityName).trim() !== '',
      render: (ctx) => (
        <FieldGroup>
          <NameField ctx={ctx} label="Facility" kind="facility" valueKey="facilityName" />
          <WhoField ctx={ctx} label="Cleaned by" valueKey="cleanedBy" />
          <CheckboxField className="py-2">
            <Checkbox checked={ctx.values.cleanedByCompany === true} onChange={(c) => ctx.patch({ cleanedByCompany: c })} />
            <Label>Cleaned by a company</Label>
          </CheckboxField>
        </FieldGroup>
      ),
    },
    {
      id: 'checklist',
      title: 'What did you find?',
      valid: () => true,
      render: ({ values, patch }) => (
        <ChecklistFieldset sections={HYGIENE_SECTIONS} items={HYGIENE_FACILITY_CHECKLIST} value={checklistOf(values)} onChange={(c) => patch({ checklist: c })} />
      ),
    },
    {
      id: 'assessment',
      title: 'Is the facility fit for use?',
      valid: () => true,
      render: (ctx) => (
        <FieldGroup>
          <YesNo label="Fit for use?" value={ctx.values.assessmentOk} onChange={(a) => ctx.patch({ assessmentOk: a })} />
          <TextField ctx={ctx} label="Notes (optional)" valueKey="assessmentNotes" multiline />
        </FieldGroup>
      ),
    },
  ],
  review: ({ values }) => {
    const c = checklistOf(values)
    return {
      title: `Hygiene facility · ${str(values.facilityName)}`,
      lines: [
        { label: 'Date', value: str(values.date) },
        { label: 'Checked', value: `${answered(c)} of ${HYGIENE_FACILITY_CHECKLIST.length} items, ${issues(c)} with an issue` },
        ...(typeof values.assessmentOk === 'boolean' ? [{ label: 'Fit for use', value: Yes(values.assessmentOk) }] : []),
        ...line('Notes', values.assessmentNotes),
        ...line('Cleaned by', values.cleanedBy),
      ],
    }
  },
  entry: (ctx) => {
    const v = ctx.values
    return dropNulls({ facilityName: str(v.facilityName).trim(), checkedOn: str(v.date), checklist: checklistOf(v), assessmentOk: typeof v.assessmentOk === 'boolean' ? v.assessmentOk : null, assessmentNotes: optText(v.assessmentNotes) ?? null, cleanedBy: optText(v.cleanedBy) ?? null, cleanedByCompany: v.cleanedByCompany === true })
  },
  badge: { framework: 'CanadaGAP', formCode: 'J', formName: 'Cleaning and Maintenance, Personal Hygiene Facilities' },
  historyPath: farmPath('hygiene-facility-checks'),
  onRecorded: rememberWho('cleanedBy'),
}

// --------------------------------------------------------------------------- Form M

export const pestCheckFlow: FlowDef = {
  kind: 'pest_check',
  checkForm: 'm',
  label: 'Pest check',
  needsField: false,
  initial: ({ today }) => ({ date: today }),
  steps: [
    {
      id: 'where',
      title: 'Where did you check?',
      valid: (v) => str(v.buildingName).trim() !== '' && str(v.deviceOrArea).trim() !== '',
      render: (ctx) => (
        <FieldGroup>
          <AssetField ctx={ctx} label="Building" kind="building" category="BUILDING" valueKey="buildingName" />
          <NameField ctx={ctx} label="Trap or area" kind="pest_device" valueKey="deviceOrArea" building={optText(ctx.values.buildingName)} />
        </FieldGroup>
      ),
    },
    {
      id: 'found',
      title: 'What did you find?',
      valid: () => true,
      render: (ctx) => (
        <FieldGroup>
          <TextField ctx={ctx} label="Findings" valueKey="findings" multiline />
          <TextField ctx={ctx} label="What did you do about it? (optional)" valueKey="actionTaken" multiline />
          <WhoField ctx={ctx} label="Who checked?" valueKey="personResponsible" />
        </FieldGroup>
      ),
    },
  ],
  review: ({ values }) => ({
    title: `Pest check · ${str(values.buildingName)}`,
    lines: [{ label: 'Date', value: str(values.date) }, { label: 'Building', value: str(values.buildingName) + linkedNote(values) }, { label: 'Trap or area', value: str(values.deviceOrArea) }, ...line('Findings', values.findings), ...line('Action', values.actionTaken), ...line('Checked by', values.personResponsible)],
  }),
  entry: (ctx) => {
    const v = ctx.values
    return dropNulls({ buildingName: str(v.buildingName).trim(), assetId: assetOf(v), checkedOn: str(v.date), deviceOrArea: str(v.deviceOrArea).trim(), findings: optText(v.findings) ?? null, actionTaken: optText(v.actionTaken) ?? null, personResponsible: optText(v.personResponsible) ?? null })
  },
  badge: { framework: 'CanadaGAP', formCode: 'M', formName: 'Pest Monitoring for Buildings' },
  historyPath: farmPath('pest-monitoring-checks'),
  onRecorded: rememberWho('personResponsible'),
}

// --------------------------------------------------------------------------- Form L

export const visitorFlow: FlowDef = {
  kind: 'visitor',
  checkForm: 'l',
  label: 'Visitor',
  needsField: false,
  initial: ({ today }) => ({ date: today, policyAcknowledged: false }),
  steps: [
    {
      id: 'who',
      title: 'Who visited?',
      valid: (v) => str(v.visitorName).trim() !== '' && v.policyAcknowledged === true,
      render: (ctx) => (
        <FieldGroup>
          <TextField ctx={ctx} label="Name" valueKey="visitorName" />
          <TextField ctx={ctx} label="Company (optional)" valueKey="companyName" />
          <TextField ctx={ctx} label="Reason for the visit" valueKey="purpose" />
          <TextField ctx={ctx} label="Where did they go?" valueKey="location" />
          <CheckboxField className="py-2">
            <Checkbox checked={ctx.values.policyAcknowledged === true} onChange={(c) => ctx.patch({ policyAcknowledged: c })} />
            <Label>They were told the visitor policy and understand it</Label>
          </CheckboxField>
        </FieldGroup>
      ),
    },
  ],
  review: ({ values }) => ({
    title: `Visitor · ${str(values.visitorName)}`,
    lines: [{ label: 'Date', value: str(values.date) }, ...line('Company', values.companyName), ...line('Reason', values.purpose), ...line('Went to', values.location), { label: 'Told the visitor policy', value: Yes(values.policyAcknowledged) }],
  }),
  entry: (ctx) => {
    const v = ctx.values
    return dropNulls({ visitedOn: str(v.date), visitorName: str(v.visitorName).trim(), policyAcknowledged: v.policyAcknowledged === true, companyName: optText(v.companyName) ?? null, purpose: optText(v.purpose) ?? null, location: optText(v.location) ?? null })
  },
  badge: { framework: 'CanadaGAP', formCode: 'L', formName: 'Visitor Sign-In Log' },
  historyPath: farmPath('visitor-entries'),
}

// --------------------------------------------------------------------------- Form K

const TOPICS = [
  { value: 'form_c', label: 'Form C', description: 'Employee personal hygiene and food handling policy, production site' },
  { value: 'form_d', label: 'Form D', description: 'Employee personal hygiene and food handling policy, packinghouse or storage' },
  { value: 'deviations', label: 'Minor and major deviations', description: 'What to do when something goes wrong' },
  { value: 'other', label: 'Something else', description: '' },
] as const
const RECORD_TYPES = [
  { value: 'casual', label: 'Casual employee' },
  { value: 'contract', label: 'Contract employee' },
  { value: 'payroll', label: 'Payroll record' },
  { value: 'signature', label: 'Signatures' },
] as const

const topicsOf = (v: StepCtx['values']): string[] => (Array.isArray(v.topics) ? (v.topics as string[]) : [])

export const trainingFlow: FlowDef = {
  kind: 'training',
  checkForm: 'k',
  label: 'Training',
  needsField: false,
  initial: ({ today }) => ({ date: today, topics: [] }),
  steps: [
    {
      id: 'topics',
      title: 'What was covered?',
      valid: (v) => topicsOf(v).length > 0,
      render: (ctx) => {
        const topics = topicsOf(ctx.values)
        return (
          <FieldGroup>
            {TOPICS.map((t) => (
              <CheckboxField key={t.value} className="py-1">
                <Checkbox checked={topics.includes(t.value)} onChange={(c) => ctx.patch({ topics: c ? [...topics, t.value] : topics.filter((x) => x !== t.value) })} />
                <Label className="font-medium">{t.label}</Label>
                {t.description && <Description>{t.description}</Description>}
              </CheckboxField>
            ))}
            {topics.includes('other') && <TextField ctx={ctx} label="What else?" valueKey="topicOther" />}
          </FieldGroup>
        )
      },
    },
    {
      id: 'who',
      title: 'Who was there?',
      valid: () => true,
      render: (ctx) => (
        <FieldGroup>
          <WhoField ctx={ctx} label="Trainer" valueKey="trainerName" />
          <TextField ctx={ctx} label="How many people?" valueKey="traineeCount" type="number" />
          <TextField ctx={ctx} label="Names" valueKey="traineeNames" multiline />
          <ChoiceButtons label="Where is the record kept?" value={str(ctx.values.recordType) as (typeof RECORD_TYPES)[number]['value'] | ''} options={RECORD_TYPES} onChange={(t) => ctx.patch({ recordType: t })} />
          <TextField ctx={ctx} label="Record reference" valueKey="recordReference" />
        </FieldGroup>
      ),
    },
  ],
  review: ({ values }) => ({
    title: 'Training',
    lines: [
      { label: 'Date', value: str(values.date) },
      { label: 'Covered', value: topicsOf(values).map((t) => TOPICS.find((x) => x.value === t)?.label ?? t).join(', ') },
      ...(optNum(values.traineeCount) != null ? [{ label: 'People', value: str(values.traineeCount) }] : []),
      ...line('Trainer', values.trainerName),
    ],
  }),
  entry: (ctx) => {
    const v = ctx.values
    return dropNulls({ heldOn: str(v.date), topics: topicsOf(v), topicOther: topicsOf(v).includes('other') ? (optText(v.topicOther) ?? null) : null, traineeCount: optNum(v.traineeCount) ?? null, traineeNames: optText(v.traineeNames) ?? null, trainerName: optText(v.trainerName) ?? null, recordType: optText(v.recordType) ?? null, recordReference: optText(v.recordReference) ?? null })
  },
  badge: { framework: 'CanadaGAP', formCode: 'K', formName: 'Training Session' },
  historyPath: farmPath('training-sessions'),
  onRecorded: rememberWho('trainerName'),
}
